import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Alert, Row, Col, Image, Modal, Form } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import BookProgress from '../components/BookProgress';
import Message from '../components/Message';
import Loader from '../components/Loader';
import { getStoryContent, getJobImages, approveFinal, getGenaiJobDetails } from '../actions/genaiActions';
import { GENAI_APPROVE_FINAL_RESET } from '../constants/genaiConstants';
import { useTranslation } from 'react-i18next';
import { getNativeLanguageName } from '../constants/languageNames';
import { getImageFrameStyle, getImageTagStyle } from '../constants/imageDisplay';

function FinalBookScreen() {
    const { jobId } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [showNoteModal, setShowNoteModal] = useState(false);
    const [showCoverTextModal, setShowCoverTextModal] = useState(false);
    const [personalNote, setPersonalNote] = useState('');
    const [coverTextColorMode, setCoverTextColorMode] = useState('color');
    const [coverTextVerticalPosition, setCoverTextVerticalPosition] = useState('top');
    const [noteSaving, setNoteSaving] = useState(false);
    const [coverTextSaving, setCoverTextSaving] = useState(false);
    const [printSending, setPrintSending] = useState(false);
    const [isReassembling, setIsReassembling] = useState(false);
    const reassemblyRequestedAt = React.useRef(null);

    const userLogin = useSelector(state => state.userLogin);
    const { userInfo } = userLogin;

    const genaiJobDetails = useSelector(state => state.genaiJobDetails);
    const { loading: loadingJob, error: errorJob, job } = genaiJobDetails;

    const genaiStory = useSelector(state => state.genaiStory);
    const { loading: loadingStory, error: errorStory, pages } = genaiStory;

    const genaiImages = useSelector(state => state.genaiImages);
    const { loading: loadingImages, error: errorImages, images } = genaiImages;

    const genaiApproveFinal = useSelector(state => state.genaiApproveFinal);
    const { loading: loadingApprove, success: successApprove } = genaiApproveFinal;
    const isTaletotsWaiting = job?.qa_mode === 'taletots' && job.status !== 'completed';

    useEffect(() => {
        if (!userInfo) {
            navigate('/login?redirect=/genai/create_story');
        } else {
            dispatch({ type: GENAI_APPROVE_FINAL_RESET });
            dispatch(getGenaiJobDetails(jobId));
            dispatch(getStoryContent(jobId));
            dispatch(getJobImages(jobId));
        }
    }, [dispatch, navigate, userInfo, jobId]);

    // Initialize personal note from job data
    useEffect(() => {
        if (!job) return;
        setPersonalNote(job.personal_note || '');
        setCoverTextColorMode(job.cover_text_color_mode || 'color');
        setCoverTextVerticalPosition(job.cover_text_vertical_position || 'top');
    }, [job?.job_id, job?.personal_note, job?.cover_text_color_mode, job?.cover_text_vertical_position]);

    // Poll for updates during PDF assembly (first-time assemble), and (for taletots-QA
    // jobs) while waiting on staff to give the final approval
    useEffect(() => {
        const waitingOnStaff = job?.qa_mode === 'taletots' && job.status === 'awaiting_approval';
        if (job && (job.status === 'assembling' || waitingOnStaff)) {
            const interval = setInterval(() => {
                dispatch(getGenaiJobDetails(jobId));
            }, 5000);
            return () => clearInterval(interval);
        }
    }, [job?.status, job?.qa_mode, dispatch, jobId]);

    // Poll for updates after personal note reassembly (status stays 'completed')
    useEffect(() => {
        if (!isReassembling) return;
        const interval = setInterval(() => {
            dispatch(getGenaiJobDetails(jobId));
        }, 4000);
        return () => clearInterval(interval);
    }, [isReassembling, dispatch, jobId]);

    // Stop polling once job.updated_at advances past when we triggered reassembly
    useEffect(() => {
        if (!isReassembling || !job?.updated_at || !reassemblyRequestedAt.current) return;
        const jobUpdated = new Date(job.updated_at).getTime();
        if (jobUpdated > reassemblyRequestedAt.current) {
            setIsReassembling(false);
        }
    }, [job?.updated_at, isReassembling]);

    useEffect(() => {
        if (successApprove) {
            // Book saved to library, redirect to My Books
            navigate('/genai/jobs');
        }
    }, [successApprove, navigate]);

    const confirmGenericGreetingIfNeeded = () => {
        const hasCustomGreeting = Boolean((personalNote || '').trim());
        if (hasCustomGreeting) {
            return true;
        }
        return window.confirm(t('finalBook.greetingUnchangedConfirm'));
    };

    const handleSaveToLibrary = () => {
        if (!confirmGenericGreetingIfNeeded()) {
            return;
        }
        dispatch(approveFinal(jobId));
    };

    const handleDownloadPDF = async () => {
        try {
            // Make authenticated request to download PDF
            const response = await fetch(`/api/genai/jobs/${jobId}/download-pdf/`, {
                headers: {
                    'Authorization': `Bearer ${userInfo.token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                alert(errorData.error || t('finalBook.pdfFailed'));
                return;
            }

            // Create blob from response and trigger download
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            
            // Get filename from Content-Disposition header or use default
            const contentDisposition = response.headers.get('Content-Disposition');
            const filenameMatch = contentDisposition && contentDisposition.match(/filename="(.+)"/);
            link.download = filenameMatch ? filenameMatch[1] : `book_${jobId}.pdf`;
            
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading PDF:', error);
            alert(t('finalBook.pdfFailed'));
        }
    };

    const handleSavePersonalNote = async () => {
        setNoteSaving(true);
        try {
            const response = await fetch(`/api/genai/jobs/${jobId}/personal-note/`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${userInfo.token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ personal_note: personalNote })
            });

            if (!response.ok) {
                const errorData = await response.json();
                alert(errorData.error || 'Failed to update personal note');
                return;
            }

            // Start polling — reassemble_pdf_task runs async, status stays 'completed'
            reassemblyRequestedAt.current = Date.now();
            setIsReassembling(true);
            setShowNoteModal(false);
        } catch (error) {
            console.error('Error saving personal note:', error);
            alert('Failed to save personal note');
        } finally {
            setNoteSaving(false);
        }
    };

    const handleSendToPrinting = async () => {
        if (!confirmGenericGreetingIfNeeded()) {
            return;
        }
        setPrintSending(true);
        try {
            const response = await fetch(`/api/genai/jobs/${jobId}/send-to-printing/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${userInfo.token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ resubmit: false })
            });

            if (!response.ok) {
                const errorData = await response.json();
                alert(errorData.error || 'Failed to send to printing');
                return;
            }

            const data = await response.json();
            alert(`✓ ${data.message}\nOrder ID: ${data.order_id}\nCopies: ${data.copies}\nEstimated Delivery: ${data.estimated_delivery}`);
            // Refresh job so print_submitted flag updates and button disables
            dispatch(getGenaiJobDetails(jobId));
        } catch (error) {
            console.error('Error sending to printing:', error);
            alert('Failed to send to printing');
        } finally {
            setPrintSending(false);
        }
    };

    const handleSaveCoverTextSettings = async () => {
        setCoverTextSaving(true);
        try {
            const response = await fetch(`/api/genai/jobs/${jobId}/cover-text-settings/`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${userInfo.token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    cover_text_color_mode: coverTextColorMode,
                    cover_text_vertical_position: coverTextVerticalPosition,
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                alert(errorData.error || 'Failed to update cover text settings');
                return;
            }

            reassemblyRequestedAt.current = Date.now();
            setIsReassembling(true);
            setShowCoverTextModal(false);
        } catch (error) {
            console.error('Error saving cover text settings:', error);
            alert('Failed to save cover text settings');
        } finally {
            setCoverTextSaving(false);
        }
    };

    // Combine pages with images for display
    const bookPages = pages && images ? 
        pages.map(page => ({
            ...page,
            image: images[page.page_number || 0]
        })).sort((a, b) => (a.page_number || 0) - (b.page_number || 0))
        : [];

    const getPageLabel = (pageNum) => {
        if (pageNum === 0) return t('finalBook.frontCoverLabel');
        if (pageNum === 13) return t('finalBook.backCoverLabel');
        return t('finalBook.pageN', { n: pageNum });
    };

    return (
        <div>
            <BookProgress step1 step2 step3 step4 jobId={jobId} />

            <h1>{t('finalBook.title')}</h1>
            <p className="text-muted">
                {t('finalBook.subtitle')}
            </p>

            {loadingJob || loadingStory || loadingImages ? (
                <Loader />
            ) : errorJob ? (
                <Message variant='danger'>{errorJob}</Message>
            ) : errorStory ? (
                <Message variant='danger'>{errorStory}</Message>
            ) : errorImages ? (
                <Message variant='danger'>{errorImages}</Message>
            ) : (
                <>
                    {/* Reassembly in progress banner */}
                    {isReassembling && (
                        <Alert variant="info" className="mb-3 d-flex align-items-center gap-2">
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            <span>{t('finalBook.reassemblingLatestSettings')}</span>
                        </Alert>
                    )}

                    {/* Job Info */}
                    {job && (
                        <Alert variant={job.status === 'assembling' ? 'info' : 'success'} className="mb-4">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <strong>
                                        {job.status === 'assembling' ? t('finalBook.assembling') : t('finalBook.complete')}
                                    </strong>
                                    <p className="mb-0 mt-1">
                                        {t('finalBook.status')} {job.status} | {t('finalBook.language')} {getNativeLanguageName(job.input_data?.language || 'English', 'English')}
                                    </p>
                                    {job.status === 'assembling' && (
                                        <small className="text-muted">
                                            {t('finalBook.autoRefresh')}
                                        </small>
                                    )}
                                </div>
                                <div className="text-end">
                                    <small className="text-muted d-block mb-2">
                                        {t('finalBook.regenerations', { count: job.user_regeneration_count || 0 })}
                                    </small>
                                    <Button 
                                        variant="outline-primary" 
                                        size="sm"
                                        onClick={handleDownloadPDF}
                                        disabled={job.status === 'assembling' || !job.pdf_url || (job.qa_mode === 'taletots' && job.status !== 'completed')}
                                    >
                                        {job.status === 'assembling' ? t('finalBook.downloadAssembling') : t('finalBook.downloadPdf')}
                                    </Button>
                                </div>
                            </div>
                        </Alert>
                    )}

                    {/* Action Buttons - Primary */}
                    {job && job.status === 'completed' ? (
                        <>
                            <Card className="mb-4 p-4 bg-light border-success" style={{ borderWidth: '2px' }}>
                                <Row className="align-items-center">
                                    <Col md={8}>
                                        <h4 className="mb-2">{t('finalBook.savedToLibrary')}</h4>
                                        <p className="mb-0 text-muted">
                                            {t('finalBook.alreadySaved')}
                                        </p>
                                    </Col>
                                    <Col md={4} className="text-end d-flex flex-column gap-2">
                                        <Button
                                            variant="primary"
                                            size="lg"
                                            onClick={handleDownloadPDF}
                                            disabled={!job.pdf_url}
                                            className="w-100"
                                        >
                                            {t('finalBook.downloadPdf')}
                                        </Button>
                                        <Button
                                            variant="outline-secondary"
                                            onClick={() => navigate('/genai/jobs')}
                                            className="w-100"
                                        >
                                            {t('finalBook.backToBooks')}
                                        </Button>
                                    </Col>
                                </Row>
                            </Card>

                            {/* Personal Note & Printing Options */}
                            <Row className="mb-4">
                                <Col md={4}>
                                    <Card className="h-100">
                                        <Card.Header className="bg-primary text-white">
                                            <h5 className="mb-0">🎨 Cover Text Settings</h5>
                                        </Card.Header>
                                        <Card.Body>
                                            <p className="text-muted">
                                                Choose color style and text position for both front and back cover titles.
                                            </p>
                                            <Button
                                                variant="outline-primary"
                                                className="w-100"
                                                onClick={() => setShowCoverTextModal(true)}
                                                disabled={isReassembling}
                                            >
                                                {isReassembling ? 'Reassembling PDF…' : 'Edit Cover Text'}
                                            </Button>
                                            <div className="mt-3 p-2 bg-light border rounded">
                                                <small className="text-muted d-block">Current settings:</small>
                                                <small className="d-block mt-1">
                                                    Style: {coverTextColorMode === 'black' ? 'Black' : 'Color'}
                                                </small>
                                                <small className="d-block">
                                                    Position: {coverTextVerticalPosition === 'bottom' ? 'Bottom' : 'Top'}
                                                </small>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col md={4}>
                                    <Card className="h-100">
                                        <Card.Header className="bg-info text-white">
                                            <h5 className="mb-0">✏️ Customize Thank You Page</h5>
                                        </Card.Header>
                                        <Card.Body>
                                            <p className="text-muted">
                                                Replace the generic thank-you message with your own personal note.
                                            </p>
                                            <Button
                                                variant="outline-info"
                                                className="w-100"
                                                onClick={() => setShowNoteModal(true)}
                                                disabled={isReassembling}
                                            >
                                                {isReassembling ? 'Reassembling PDF…' : 'Edit Thank You Page'}
                                            </Button>
                                            {personalNote && (
                                                <div className="mt-3 p-2 bg-light border rounded">
                                                    <small className="text-muted">Current note:</small>
                                                    <p className="mb-0 mt-1" style={{ fontSize: '0.9rem' }}>
                                                        {personalNote}
                                                    </p>
                                                </div>
                                            )}
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col md={4}>
                                    <Card className="h-100">
                                        <Card.Header className="bg-success text-white">
                                            <h5 className="mb-0">📦 Send to Printing</h5>
                                        </Card.Header>
                                        <Card.Body>
                                            <p className="text-muted">
                                                Order printed copies of your book to be delivered to your home.
                                            </p>
                                            {job.print_submitted ? (
                                                <div className="text-center">
                                                    <div className="text-success fw-bold mb-1">✓ Sent to printing</div>
                                                    {job.print_provider_order_id && (
                                                        <small className="text-muted d-block">Provider order: {job.print_provider_order_id}</small>
                                                    )}
                                                    <small className="text-muted d-block mt-3">Resubmission is available from the admin page.</small>
                                                    <small className="text-muted d-block mt-1">Estimated delivery: 7-10 business days</small>
                                                </div>
                                            ) : (
                                                <>
                                                    <Button
                                                        variant="outline-success"
                                                        className="w-100"
                                                        onClick={handleSendToPrinting}
                                                        disabled={printSending}
                                                    >
                                                        {printSending ? 'Processing...' : '📦 Send to Printing'}
                                                    </Button>
                                                    <small className="text-muted d-block mt-2">
                                                        Estimated delivery: 7-10 business days
                                                    </small>
                                                </>
                                            )}
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>
                        </>
                    ) : job?.qa_mode === 'taletots' ? (
                        <Card className="mb-4 p-4 bg-light border-info" style={{ borderWidth: '2px' }}>
                            <h4 className="mb-2">{t('finalBook.staffFinalCheckTitle')}</h4>
                            <p className="mb-0 text-muted">
                                {t('finalBook.staffFinalCheckBody')}
                            </p>
                        </Card>
                    ) : (
                        <Card className="mb-4 p-4 bg-light border-success" style={{ borderWidth: '2px' }}>
                            <Row className="align-items-center">
                                <Col md={8}>
                                    <h4 className="mb-2">{t('finalBook.readyToSave')}</h4>
                                    <p className="mb-0 text-muted">
                                        {t('finalBook.saveHint')}
                                    </p>
                                </Col>
                                <Col md={4} className="text-end">
                                    <Button 
                                        variant="success" 
                                        size="lg"
                                        onClick={handleSaveToLibrary}
                                        disabled={loadingApprove}
                                        className="mb-2 w-100"
                                    >
                                        {loadingApprove ? t('finalBook.saving') : t('finalBook.saveBtn')}
                                    </Button>
                                </Col>
                            </Row>
                        </Card>
                    )}

                    {/* Book Preview */}
                    <Card className="mb-4">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h4 className="mb-0">{t('finalBook.preview')}</h4>
                            {!job?.completed_at && (
                                <div className="d-flex gap-2">
                                    <Button 
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => navigate(`/genai/job/${jobId}/review-pages`)}
                                    >
                                        {t('finalBook.backToPages')}
                                    </Button>
                                </div>
                            )}
                        </Card.Header>
                        <Card.Body>
                            {bookPages.length > 0 ? (
                                <div className="book-preview">
                                    {/* Cover — hidden once the book has been saved to library */}
                                    {!job?.completed_at && bookPages.filter(p => (p.page_number || 0) === 0).map(page => (
                                        <Card key={page.page_number} className="mb-4 shadow-lg border-primary">
                                            <Card.Header className="bg-primary text-white">
                                                <h4 className="mb-0">📖 {getPageLabel(page.page_number)}</h4>
                                            </Card.Header>
                                            <Card.Body>
                                                <Row>
                                                    <Col md={6} className="mx-auto text-center">
                                                        {page.image && (
                                                            <div style={getImageFrameStyle(page.image, page.page_number, { maxHeightCover: 500, maxHeightStory: 500 })}>
                                                                <Image 
                                                                    src={page.image.url || page.image.image_url} 
                                                                    alt={t('finalBook.frontCoverAlt')}
                                                                    fluid
                                                                    className="border border-3 rounded shadow"
                                                                    style={getImageTagStyle()}
                                                                />
                                                            </div>
                                                        )}
                                                        <h5 className="mt-3">{page.text}</h5>
                                                    </Col>
                                                </Row>
                                            </Card.Body>
                                        </Card>
                                    ))}

                                    {/* Interior Pages */}
                                    {!isTaletotsWaiting && <Row>
                                        {bookPages
                                            .filter(p => (p.page_number || 0) > 0 && (p.page_number || 0) < 13)
                                            .map(page => (
                                            <Col key={page.page_number} md={6} className="mb-4">
                                                <Card className="h-100 shadow">
                                                    <Card.Header>
                                                        <strong>{getPageLabel(page.page_number)}</strong>
                                                    </Card.Header>
                                                    <Card.Body>
                                                        {page.image && (
                                                            <div className="text-center mb-3">
                                                                <div style={getImageFrameStyle(page.image, page.page_number, { maxHeightCover: 300, maxHeightStory: 300 })}>
                                                                    <Image 
                                                                        src={page.image.url || page.image.image_url} 
                                                                        alt={t('finalBook.pageN', { n: page.page_number })}
                                                                        fluid
                                                                        className="border rounded"
                                                                        style={getImageTagStyle()}
                                                                    />
                                                                </div>
                                                            </div>
                                                        )}
                                                        <p className="text-center" style={{ fontSize: '0.95rem' }}>
                                                            {page.text}
                                                        </p>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        ))}
                                    </Row>}

                                    {/* Back Cover */}
                                    {!isTaletotsWaiting && bookPages.filter(p => (p.page_number || 0) === 13).map(page => (
                                        <Card key={page.page_number} className="mb-4 shadow-lg border-secondary">
                                            <Card.Header className="bg-secondary text-white">
                                                <h4 className="mb-0">📕 {getPageLabel(page.page_number)}</h4>
                                            </Card.Header>
                                            <Card.Body>
                                                <Row>
                                                    <Col md={6} className="mx-auto text-center">
                                                        {page.image && (
                                                            <div style={getImageFrameStyle(page.image, page.page_number, { maxHeightCover: 500, maxHeightStory: 500 })}>
                                                                <Image 
                                                                    src={page.image.url || page.image.image_url} 
                                                                    alt={t('finalBook.backCoverAlt')}
                                                                    fluid
                                                                    className="border border-3 rounded shadow"
                                                                    style={getImageTagStyle()}
                                                                />
                                                            </div>
                                                        )}
                                                        <h5 className="mt-3">{page.text}</h5>
                                                    </Col>
                                                </Row>
                                            </Card.Body>
                                        </Card>
                                    ))}
                                </div>
                            ) : (
                                <Alert variant="warning">
                                    {t('finalBook.previewUnavailable')}
                                </Alert>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Secondary Action - Reject */}
                    <div className="text-center mb-4">
                        <p className="text-muted">
                            {t('finalBook.notHappy')}
                        </p>
                    </div>
                </>
            )}

            {/* Personal Note Modal */}
            <Modal show={showNoteModal} onHide={() => setShowNoteModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Customize Thank You Page</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Personal Message</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={6}
                            placeholder="Write a special message for the thank-you page... (max 500 characters)"
                            value={personalNote}
                            onChange={(e) => setPersonalNote(e.target.value.slice(0, 500))}
                        />
                        <small className="text-muted">
                            {personalNote.length}/500 characters
                        </small>
                    </Form.Group>
                    <Alert variant="info" className="small">
                        <strong>Note:</strong> Your custom message will replace the generic thank-you text on the thank-you page. The PDF will be reassembled automatically.
                    </Alert>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => {
                        setShowNoteModal(false);
                        setPersonalNote(job?.personal_note || '');
                    }}>
                        Cancel
                    </Button>
                    <Button 
                        variant="danger" 
                        onClick={() => {
                            setPersonalNote('');
                            handleSavePersonalNote();
                        }}
                        disabled={noteSaving}
                    >
                        Use Generic Thank You
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleSavePersonalNote}
                        disabled={noteSaving || !personalNote}
                    >
                        {noteSaving ? 'Saving...' : 'Save & Reassemble'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showCoverTextModal} onHide={() => setShowCoverTextModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Customize Cover Text</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Text style (front + back)</Form.Label>
                        <div>
                            <Form.Check
                                type="radio"
                                id="cover-text-color"
                                name="cover-text-style"
                                label="Color (current multicolor style)"
                                checked={coverTextColorMode === 'color'}
                                onChange={() => setCoverTextColorMode('color')}
                            />
                            <Form.Check
                                type="radio"
                                id="cover-text-black"
                                name="cover-text-style"
                                label="Black"
                                checked={coverTextColorMode === 'black'}
                                onChange={() => setCoverTextColorMode('black')}
                            />
                        </div>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Text position (front + back)</Form.Label>
                        <div>
                            <Form.Check
                                type="radio"
                                id="cover-text-top"
                                name="cover-text-position"
                                label="Top (current)"
                                checked={coverTextVerticalPosition === 'top'}
                                onChange={() => setCoverTextVerticalPosition('top')}
                            />
                            <Form.Check
                                type="radio"
                                id="cover-text-bottom"
                                name="cover-text-position"
                                label="Bottom"
                                checked={coverTextVerticalPosition === 'bottom'}
                                onChange={() => setCoverTextVerticalPosition('bottom')}
                            />
                        </div>
                    </Form.Group>

                    <Alert variant="info" className="small mb-0">
                        <strong>Note:</strong> This will reassemble your PDF only. Artwork and story text are not regenerated.
                    </Alert>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => {
                        setShowCoverTextModal(false);
                        setCoverTextColorMode(job?.cover_text_color_mode || 'color');
                        setCoverTextVerticalPosition(job?.cover_text_vertical_position || 'top');
                    }}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSaveCoverTextSettings}
                        disabled={coverTextSaving || isReassembling}
                    >
                        {coverTextSaving ? 'Saving...' : 'Save & Reassemble'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default FinalBookScreen;
